import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BRAND } from '../generated/brand';
import { brandInitials, useCompanyBrand } from '../utils/company-brand';

/** Ícono de Ruttia (el de la landing) para cuando no hay empresa elegida ni marca de compilación. */
export const RuttiaIcon: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" style={{ flex: 'none' }}>
    <rect width="32" height="32" rx="7" fill="#0B2545" />
    <path d="M8 24c5-1 5-8 10-9s6-6 7-8" fill="none" stroke="#3FB8A9" strokeWidth="3" strokeLinecap="round" />
    <circle cx="8" cy="24" r="2.6" fill="#3FB8A9" />
    <circle cx="25" cy="7" r="2.6" fill="#E3A13B" />
  </svg>
);

/** Distintivo con las iniciales de la empresa, en su color, cuando no tiene logo. */
const Initials: React.FC<{ name: string; color: string; size: number }> = ({ name, color, size }) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      flex: 'none',
      borderRadius: `${Math.round(size * 0.25)}px`,
      bgcolor: color,
      color: (t) => t.palette.getContrastText(color),
      display: 'grid',
      placeItems: 'center',
      fontWeight: 800,
      fontSize: Math.round(size * 0.42),
      lineHeight: 1,
    }}
  >
    {brandInitials(name)}
  </Box>
);

/** Nombre que se muestra: el de la empresa en uso, o el de la compilación. */
export const useBrandName = () => useCompanyBrand()?.name ?? BRAND.displayName;

/**
 * Marca para el ingreso (`full`: logo grande o distintivo + nombre) y la barra superior (`mark`: solo el
 * distintivo). Orden: logo de la empresa (portal) → marca de la compilación → iniciales de la empresa → Ruttia.
 */
export const BrandMark: React.FC<{ variant: 'full' | 'mark'; color?: string }> = ({ variant, color }) => {
  const company = useCompanyBrand();
  const buildLogo = variant === 'full' ? BRAND.web.assetUrls.logoFull : BRAND.web.assetUrls.logoMark;
  const name = company?.name ?? BRAND.displayName;

  if (variant === 'mark') {
    if (company?.logoUrl) return <Box component="img" src={company.logoUrl} alt="" sx={{ width: 30, height: 30, objectFit: 'contain', mr: 1, flex: 'none' }} />;
    if (company)
      return (
        <Box sx={{ mr: 1, display: 'flex' }}>
          <Initials name={company.name} color={company.primary} size={30} />
        </Box>
      );
    if (buildLogo) return <Box component="img" src={buildLogo} alt="" sx={{ width: 30, height: 30, objectFit: 'contain', mr: 1, flex: 'none' }} />;
    return (
      <Box sx={{ mr: 1, display: 'flex' }}>
        <RuttiaIcon size={30} />
      </Box>
    );
  }

  if (company?.logoUrl || (!company && buildLogo)) {
    return (
      <Box
        component="img"
        src={company?.logoUrl || buildLogo}
        alt={name}
        sx={{ display: 'block', width: { xs: 200, sm: 240 }, maxWidth: '100%', maxHeight: 96, height: 'auto', objectFit: 'contain', mx: 'auto', mb: 3 }}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 3, minWidth: 0 }}>
      {company ? <Initials name={company.name} color={company.primary} size={44} /> : <RuttiaIcon size={44} />}
      <Typography component="span" sx={{ fontWeight: 800, fontSize: 22, color: color ?? 'text.primary', minWidth: 0, overflowWrap: 'anywhere' }}>
        {company ? company.name : 'ruttia'}
      </Typography>
    </Box>
  );
};
